/**
 * @module common/components/single_entry_header
 */
import React, { PropTypes, Component } from 'react';
import classNames from 'classnames';
import Icon from 'common/components/svg_icon';

/**
 * Abstract single entry header component. Handles toggling dropdown
 * and rendering of header using data from props.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
class SingleEntryHeader extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dropdownVisible: false,
    };

    this.toggleDropdown = this.toggleDropdown.bind(this);
  }

  toggleDropdown() {
    this.setState({
      dropdownVisible: !this.state.dropdownVisible,
    });
  }

  /**
   * Renders single entry dropdown using data from props. Data should be an array
   * of jsx button elements.
   *
   * @return {node} - rendered dropdown node
   */
  renderEntryDropdown() {
    if (this.props.dropdownData && this.state.dropdownVisible) {
      return (
        <div className="entry-dropdown-wrapper">
          <ul>
            { this.props.dropdownData }
          </ul>
        </div>
      );
    }

    return null;
  }

  /**
   * Renders top header element using data from props. Data should be an object
   * with required params :
   * {
   *   leftElement: PropTypes.node,
   *   pageNumber: PropTypes.number,
   *   itemsCount: PropTypes.number,
   *   onNextPage: PropTypes.func,
   *   onPrevPage: PropTypes.func,
   *   onToggleDetails: PropTypes.func,
  *    hidePager: PropTypes.bool
   * }
   *
   * @return {node} - rendered top header element
   */
  renderTopHeader() {
    const { leftElement, pageNumber, itemsCount, hidePager,
      onNextPage, onPrevPage, onToggleDetails } = this.props.topHeaderData;

    return (
      <div className="row expanded">
        <div className="small-12 medium-4 large-6 column align-self-bottom">
          { leftElement }
        </div>
        <div className="hide-for-small-only medium-8 large-4 column align-self-bottom entry-pagination">
          {!hidePager &&
          <div className="pager">
            <button
              className={classNames("btn btn-icon-only prev", {
                'invisible' : pageNumber === 1,
              })}
              onClick={onPrevPage}
            >
              <Icon
                className={classNames('svg-icon')}
                icon="arrowLeft"
              />
            </button>
            <span className="page-numbers">
              {pageNumber} of { itemsCount }
            </span>
            <button
              className={classNames("btn btn-icon-only next",{
                'invisible' : pageNumber === itemsCount,
              })}
              onClick={onNextPage}
            >
              <Icon className={classNames('svg-icon')} icon="arrowRight"/>
            </button>
          </div>}
        </div>
        <div className="small-4 medium-2 large-1 medium-offset-2
          large-offset-5 column align-self-top"
        >
          <div className="button-wrapper text-right">
            <button
              type="button"
              onClick={onToggleDetails}
              className="btn btn-icon-only btn-close"
            >
              <Icon className={classNames('svg-icon')} icon="close"/>
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Renders bottom header element using data from props. Data should be an object
   * with two keys:
   * {
   *   leftControls: PropTypes.array - array of control buttons,
   *   rater: PropTypes.node - ReactRating element,
   * }
   *
   * @return {node} - rendered bottom header element
   */
  renderBottomHeader() {
    const { bottomHeaderData, dropdownData, renderDropdownMenu } = this.props;
    const rightControls = bottomHeaderData.rightControls || [];

    if (bottomHeaderData) {
      return (
        <div className="row expanded">
          <div className="show-for-large
            large-6 column entry-controls entry-left-controls align-self-bottom"
          >
            {bottomHeaderData.leftControls}
          </div>
          <div className="small-12 medium-8 medium-offset-4 large-offset-0
            large-4 column entry-rating"
          >
            <div className="project-entry-rating">
              {bottomHeaderData.rater}
            </div>
            { bottomHeaderData.centerElement }
          </div>
          <div className="small-4 medium-2 medium-offset-2
            large-offset-4 column entry-controls entry-right-controls align-self-bottom"
          >
            { rightControls.map(button => button) }
            {dropdownData && <div className="button-wrapper entry-tools-button">
                <button
                  type="button"
                  className={classNames('btn btn-utility btn-icon-only',
                    { selected: this.state.entryDropdownVisible })}
                  onClick={this.toggleDropdown}
                >
                  <i className="fa fa-ellipsis-v" />
                </button>
            </div>}
            {renderDropdownMenu && renderDropdownMenu()}
          </div>
        </div>
      );
    }

    return null;
  }

  render() {
    const elementClasses = classNames('entry-header', this.props.cssClasses || '');

    return (
      <header className={elementClasses}>
        { this.props.topHeaderData &&
          <div className="header-top">
            {this.renderTopHeader()}
          </div>
        }
        { this.props.bottomHeaderData &&
          <div className="header-bottom">
            {this.renderBottomHeader()}
          </div>
        }
        {this.renderEntryDropdown()}
      </header>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {object} topHeaderData - object with data to render top header element
 * @property {object} bottomHeaderData - object with data to render bottom header element
 * @property {object} dropdownData - object with data to render dropdown element
 */
SingleEntryHeader.propTypes = {
  cssClasses: PropTypes.string,
  topHeaderData: PropTypes.object,
  bottomHeaderData: PropTypes.object,
  dropdownData: PropTypes.array,
  renderDropdownMenu: PropTypes.func,
};

export default SingleEntryHeader;



// WEBPACK FOOTER //
// ./common/components/modal/single_entry_header.jsx